"use client";
import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import FreeDeliveryBanner from "./index";
import { PageTypes } from "../../../utils/fnpPageType";
import { formatClassNames } from "../../../utils/common";
import { getDocumentScrollY } from "../../../utils/document";
import Slider from "react-slick";
import { SlickNextArrow, SlickPrevArrow } from "../carouselHelpers";

/**
 * FreeDeliveryBannerWrapper component that integrates AB testing with the banner.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isMobile - Whether the device is mobile
 * @param {string} props.fromPage - The page from which the banner is rendered
 * @param {boolean} [props.isMicrosite] - Whether the current page is a microsite
 * @param {Object} props.bannerData - The banner data containing image URLs
 * @returns {JSX.Element|null} The banner component or null if AB testing is disabled
 */
const FreeDeliveryBannerWrapper = ({ isMobile, fromPage, isMicrosite, bannerData }) => {
  const sliderSetting = {
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    infinite: true,
    autoplaySpeed: 7000,
    fade: true,
    cssEase: "linear",
    speed: 100,
    nextArrow: (
      <SlickNextArrow
        right="30px"
        width={16}
        height={16}
        customStyle={{
          position: "absolute",
          top: "43%",
          height: "30px !important",
          width: "30px !important",
        }}
      />
    ),
    prevArrow: (
      <SlickPrevArrow
        left="30px"
        width={16}
        height={16}
        customStyle={{
          position: "absolute",
          top: "43%",
          height: "30px !important",
          width: "30px !important",
        }}
      />
    ),
  };
  const wasBodyScrollLockedRef = useRef(false);

  const applyBannerScrollLayout = useCallback(() => {
    const banner = document.getElementById("banner-child");
    const header = document.getElementById("header-child");
    if (!banner || !header) {
      return;
    }

    const bannerHeight = banner.offsetHeight;
    const scrollY = getDocumentScrollY();
    const offset = Math.min(scrollY, bannerHeight);
    banner.style.transform = `translateY(-${offset}px)`;
    banner.style.transition = "transform 0s";
    header.style.marginTop = `${bannerHeight - offset}px`;
    header.style.setProperty("top", "0", "important");
  }, []);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    applyBannerScrollLayout();
    window.addEventListener("scroll", applyBannerScrollLayout, { passive: true });

    // Body scroll lock resets window.scrollY. Re-apply the saved offset when a drawer locks or unlocks.
    const bodyStyleObserver = new MutationObserver(() => {
      const isBodyFixed = document.body.style.position === "fixed";
      if (wasBodyScrollLockedRef.current !== isBodyFixed) {
        requestAnimationFrame(applyBannerScrollLayout);
      }
      wasBodyScrollLockedRef.current = isBodyFixed;
    });
    wasBodyScrollLockedRef.current = document.body.style.position === "fixed";
    bodyStyleObserver.observe(document.body, { attributes: true, attributeFilter: ["style"] });

    return () => {
      window.removeEventListener("scroll", applyBannerScrollLayout);
      bodyStyleObserver.disconnect();
    };
  }, [isMobile, bannerData, applyBannerScrollLayout]);

  return (isMobile && bannerData?.mobile) || (!isMobile && !!bannerData?.desktop?.length) ? (
    <div
      className={formatClassNames(
        "w-full transition-all duration-200",
        fromPage === PageTypes.HOME && isMobile ? "-mt-8 md:-mb-64 md:mt-32 xxl:-mb-76" : isMobile && "mt-12",
        fromPage !== PageTypes.HOME && isMicrosite && isMobile && "mb-12",
        isMobile ? "px-16" : "fixed top-0 z-[100]",
      )}
      id="banner-wrapper"
    >
      <div
        className={formatClassNames(
          "relative w-full overflow-hidden sm:h-[55px]",
          isMobile ? "h-[57px] rounded-lg" : "h-48 xml:h-[50.50px] xmll:h-[54px] xll:h-[59px] xxl:h-[67px]",
        )}
        id="banner-child"
      >
        <div className="absolute inset-0">
          {isMobile ? (
            <FreeDeliveryBanner isMobile={isMobile} bannerData={bannerData} />
          ) : Array.isArray(bannerData?.desktop) && bannerData?.desktop?.length > 1 ? (
            <Slider {...sliderSetting} className="relative w-full">
              {bannerData?.desktop?.map((banner, index) => (
                <div key={index}>
                  <FreeDeliveryBanner isMobile={isMobile} bannerData={banner} />
                </div>
              ))}
            </Slider>
          ) : (
            <FreeDeliveryBanner
              isMobile={isMobile}
              bannerData={Array.isArray(bannerData?.desktop) ? bannerData?.desktop[0] : bannerData?.desktop}
            />
          )}
        </div>
      </div>
    </div>
  ) : null;
};

FreeDeliveryBannerWrapper.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  fromPage: PropTypes.string.isRequired,
  isMicrosite: PropTypes.bool,
  bannerData: PropTypes.object.isRequired,
};

export default FreeDeliveryBannerWrapper;
